import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

import { createSession, type AuthFailure, type Session } from '../../lib/auth';
import { loadConfig } from '../../lib/config';
import { t } from '../../i18n';

import type { SessionState } from './useSession';

/**
 * The full auth surface for the three route components -- `session` carries
 * `signIn`, `signUp`, `signInWithGoogle`, `sendPasswordReset`, `signOut`
 * (`src/lib/auth.ts`'s `Session`, unmodified) -- plus the derived, read-only
 * `state` that `useSession` exposes on its own. One context, not two: a
 * second `createSession` call per consumer would open a second Firebase app
 * under the same default name and throw.
 */
export interface AuthContextValue {
  session: Session;
  state: SessionState;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const INITIAL_STATE: SessionState = { status: 'loading', user: null };

export function AuthProvider({ children }: { children: ReactNode }): ReactNode {
  // Created once per provider instance, not per render: a fresh `createSession`
  // call on every render would re-`initializeApp` the same default Firebase app
  // and throw on the second render.
  const [session] = useState<Session>(() => createSession(loadConfig()));
  const [state, setState] = useState<SessionState>(INITIAL_STATE);

  useEffect(() => {
    return session.onChange((user) => {
      setState(
        user === null
          ? { status: 'signed-out', user: null }
          : {
              status: 'signed-in',
              user: {
                email: user.email,
                uid: user.uid,
                emailVerified: user.emailVerified,
                displayName: user.displayName,
              },
            },
      );
    });
  }, [session]);

  const value = useMemo<AuthContextValue>(() => ({ session, state }), [session, state]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Reaches `signIn`/`signUp`/`signInWithGoogle`/`sendPasswordReset`/`signOut`.
 * The three auth route components use it for the first four; `AcceptInvitation`
 * calls `signOut` on it directly, in place, to swap identity without leaving
 * the screen. `useSession` and `useSignOut` (`./useSession.ts`) are built on
 * top of it for the shell, which reaches them instead -- not part of the
 * frozen contract the shell agent codes against -- so this hook stays free to
 * change shape without touching that contract.
 */
export function useAuthSession(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx === null) {
    throw new Error('useAuthSession must be used within an AuthProvider');
  }
  return ctx;
}

/**
 * One error-copy mapping shared by all three auth route components, so
 * `sign-in.tsx`, `sign-up.tsx` and `reset-password.tsx` render the same
 * sentence for the same `AuthFailure` rather than three independently
 * drifting ones. Never takes the raw thrown error -- only the already-mapped
 * `AuthFailure` -- so there is no path through this function for a raw
 * Firebase code to reach a tenant.
 */
export function describeAuthFailure(failure: AuthFailure): string {
  switch (failure) {
    case 'invalid-credentials':
      return t('auth.error.invalidCredentials');
    case 'email-in-use':
      return t('auth.error.emailInUse');
    case 'weak-password':
      return t('auth.error.weakPassword');
    case 'popup-closed':
      return t('auth.error.popupClosed');
    case 'network':
      return t('auth.error.network');
    case 'too-many-requests':
      return t('auth.error.tooManyRequests');
    case 'account-exists':
      return t('auth.error.accountExists');
    case 'unknown':
      return t('auth.error.generic');
  }
}
